import React, { useEffect } from "react";
import {
  DataGridPremium,
  GridActionsCellItem,
  GridColDef,
  GridColumnMenu,
  GridColumnMenuProps,
  GridRowId,
  GridRowModel,
  GridRowModes,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { Cancel, Save, Edit } from "@material-ui/icons";
import { Container, Grid, Typography, createSvgIcon } from "@material-ui/core";
import { useAppSelector, useAppDispatch } from "../../../app/hooks";

import { useStyles } from "./ipVerwaltungStyles";
import { globalStyles } from "../../../assets/globalStyles";
import {
  firewallRequestGenerateNI,
  firewallRequestGenerateSI,
  getFirewallRequest,
  saveFirewallRequest,
  selectFilterModel,
  selectFirewallRequestList,
  selectIsDokumentLoading,
  selectRowModesModel,
  setFilterModel,
  setRowModesModel,
} from "./ipVerwaltungSlice";
import { isAuthorized, rollen } from "../../../utils/auth/authConfig";

const NIIcon = createSvgIcon(
  <svg viewBox="6.076 6.9305 13.7947 15.4286" xmlns="http://www.w3.org/2000/svg">
    <text
      style={{ fontSize: "13.8px" }}
      x="6.076"
      y="19.502"
      transform="matrix(0.9999999999999999, 0, 0, 0.9999999999999999, 0, 0)"
    >
      NI
    </text>
  </svg>,
  "NI"
);

const SIIcon = createSvgIcon(
  <svg viewBox="6.076 6.9305 13.7947 15.4286" xmlns="http://www.w3.org/2000/svg">
    <text
      style={{ fontSize: "13.8px" }}
      x="6.076"
      y="19.502"
      transform="matrix(0.9999999999999999, 0, 0, 0.9999999999999999, 0, 0)"
    >
      SI
    </text>
  </svg>,
  "SI"
);
function CustomColumnMenu(props: Readonly<GridColumnMenuProps>) {
  return (
    <GridColumnMenu
      {...props}
      slots={{
        columnMenuGroupingItem: null,
      }}
    />
  );
}

export const IpVerwaltungGrid: React.FC = () => {
  const dispatch = useAppDispatch();

  const classes = {
    ...useStyles(),
    ...globalStyles(),
  };
  const apiRef = useGridApiRef();

  const firewallRequestList = useAppSelector(selectFirewallRequestList);
  const isFirewallRequestLoading = useAppSelector(selectIsDokumentLoading);

  const filterModel = useAppSelector(selectFilterModel);
  const rowModesModel = useAppSelector(selectRowModesModel);

  const handleEditClick = (id: GridRowId) => () => {
    dispatch(setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } }));
  };

  const handleSaveClick = (id: GridRowId) => () => {
    dispatch(setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } }));
  };

  const handleCancelClick = (id: GridRowId) => () => {
    dispatch(
      setRowModesModel({
        ...rowModesModel,
        [id]: { mode: GridRowModes.View, ignoreModifications: true },
      })
    );
  };
  const processRowUpdate = (updatedRow: GridRowModel) => {
    dispatch(saveFirewallRequest(updatedRow.messgeraetID, updatedRow));
    return updatedRow;
  };

  useEffect(() => {
    dispatch(getFirewallRequest());
  }, []);

  const dataGridCols2: GridColDef[] = [
    {
      field: "umspannwerkName",
      headerName: "Umspannwerk",
      flex: 1,
    },
    {
      field: "schaltfledName",
      headerName: "Schaltfeld",
      flex: 1,
    },
    { field: "messstelle", headerName: "Name", flex: 1 },
    { field: "macadresse", headerName: "MAC-Adresse", flex: 1 },
    { field: "ipadresse", headerName: "IP-Adresse", flex: 1 },
    { field: "subnetzmaske", headerName: "Subnetzmaske", flex: 1 },
    {
      field: "hostName",
      headerName: "Hostname",
      flex: 1,
      editable: true,
    },
    {
      field: "defaultGateway",
      headerName: "Default Gateway",
      flex: 1,
      editable: true,
    },
    {
      field: "anschlussport",
      headerName: "Anschlussport",
      flex: 1,
      editable: true,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Aktionen",
      width: 100,
      align: "right",
      getActions: (params: { id: GridRowId }) => {
        if (apiRef.current.getRowNode(params.id)?.type === "group") {
          const substation = (params.id as string).split("/")[1];
          const measuringPointsOfSubstation = firewallRequestList?.filter((x) => x.umspannwerkName === substation);
          const count = measuringPointsOfSubstation.length;
          const countWithCompletedFields = measuringPointsOfSubstation.filter(
            (x) => x.ipadresse && x.subnetzmaske && x.hostName && x.defaultGateway && x.anschlussport
          ).length;
          let statusColor = "inherit";
          if (countWithCompletedFields > 0) statusColor = "#ffd27f";
          if (countWithCompletedFields === count) statusColor = "#99cc99";
          return [
            <GridActionsCellItem
              key="1"
              icon={<SIIcon />}
              sx={{ color: statusColor }}
              label="Firewallantrag für SI herunterladen"
              onClick={() => dispatch(firewallRequestGenerateSI((params.id as string).split("/")[1]))}
            />,
            <GridActionsCellItem
              key="2"
              icon={<NIIcon />}
              sx={{ color: statusColor }}
              label="Firewallantrag für NI herunterladen"
              onClick={() => dispatch(firewallRequestGenerateNI((params.id as string).split("/")[1]))}
            />,
          ];
        }

        const isInEditMode = rowModesModel[params.id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              key="1"
              icon={<Save />}
              label="Speichern"
              color="inherit"
              onClick={handleSaveClick(params.id)}
            />,
            <GridActionsCellItem
              key="2"
              icon={<Cancel />}
              label="Verwerfen"
              onClick={handleCancelClick(params.id)}
              color="inherit"
            />,
          ];
        }

        return isAuthorized([rollen.admin])
          ? [
              <GridActionsCellItem
                key="1"
                icon={<Edit />}
                label="Bearbeiten"
                onClick={handleEditClick(params.id)}
                color="inherit"
              />,
            ]
          : [];
      },
    },
  ];

  return (
    <Container maxWidth={false} className={classes.root}>
      <div style={{ height: "82vh", width: "100%", marginTop: "10px" }}>
        <Container maxWidth={false} style={{ marginTop: "10px", marginBottom: "10px" }}>
          <Grid container md={12} spacing={2} style={{ marginBottom: "10px" }}>
            <Grid item md={3} />
            <Grid item md={6}>
              <Typography color="primary" variant="h5">
                IP Verwaltung
              </Typography>
            </Grid>
          </Grid>
        </Container>
        <DataGridPremium
          apiRef={apiRef}
          columns={dataGridCols2}
          rows={firewallRequestList ?? []}
          loading={isFirewallRequestLoading}
          getRowId={(row) => row.messgeraetID}
          initialState={{
            rowGrouping: {
              model: ["umspannwerkName"],
            },
            columns: { columnVisibilityModel: { umspannwerkName: false } },
          }}
          groupingColDef={{
            flex: 1,
          }}
          slots={{ columnMenu: CustomColumnMenu }}
          sx={{
            maxHeight: "80vh",
            "& .MuiDataGrid-cell--editable": {
              "& .MuiInputBase-root": {
                height: "100%",
              },
            },
            "& .Mui-error": {
              backgroundColor: `rgb(126,10,15, 0.1)`,
              color: "#750f0f",
            },
          }}
          filterModel={filterModel}
          onFilterModelChange={(newFilterModel) => dispatch(setFilterModel(newFilterModel))}
          disableColumnSelector
          disableColumnReorder
          disableColumnPinning
          disableAggregation
          disableRowSelectionOnClick
          hideFooterSelectedRowCount
          editMode="row"
          rowModesModel={rowModesModel}
          onRowModesModelChange={(newRowModesModel) => dispatch(setRowModesModel(newRowModesModel))}
          processRowUpdate={processRowUpdate}
        />
      </div>
    </Container>
  );
};
